import { useLayoutEffect, useRef, useState } from "react";
import { BLENDE_REDUZIERT, DAUER, bewegungReduziert, kurve, spiel, still } from "./bewegung";
import { useWelt } from "./einstellungen";

/** Zahl mit tabellarischen Ziffern. `art="rollen"`: die alte Zahl geht 420 ms mit 40 % Versatz (ruhig 45 %), die neue
 *  kommt 640 ms herein, Richtung nach Zu- oder Abnahme; `art="blende"`: Wechsel als Blende 380 ms (Offen-Block ruhig).
 *  Reduzierte Bewegung und `?still`: die neue Zahl steht sofort.
 *  `hochzaehlen` (D-420, R10-Verhalten): beim Auftritt zählt sie von 0 auf ihren Wert (760 ms, expo, mit `format`), bei
 *  reduzierter Bewegung blendet sie nur ein (120 ms), mit `?still` steht sie sofort. `warten` (FW21, Fristen-Modul): sie
 *  steht auf 0, bis das Warten endet, dann zählt sie nach `verzug` los; fällt `hochzaehlen` weg, steht der Endwert. */
export function Zahl(props: {
  wert: number;
  text?: string;
  art?: "rollen" | "blende";
  className?: string;
  kennung?: string;
  hochzaehlen?: boolean;
  format?: (n: number) => string;
  verzug?: number;
  warten?: boolean;
  /** Bezug des Zählens auf der Zeitleiste (ms), etwa der Beginn der ersten Säule; `verzug` zählt dann ab dort */
  takt?: (el: HTMLElement | null) => Promise<number>;
}) {
  const [welt] = useWelt();
  const [alt, setAlt] = useState<{ wert: number; text: string } | null>(null);
  const vorher = useRef({ wert: props.wert, text: props.text ?? String(props.wert) });
  const neu = useRef<HTMLSpanElement>(null);
  const weg = useRef<HTMLSpanElement>(null);
  const text = props.text ?? String(props.wert);

  useLayoutEffect(() => {
    const war = vorher.current;
    vorher.current = { wert: props.wert, text };
    if (war.wert === props.wert || still()) {
      setAlt(null);
      return;
    }
    setAlt(war);
  }, [props.wert, text]);

  useLayoutEffect(() => {
    if (!alt) return;
    const auf = props.wert > alt.wert ? -1 : 1;
    if (props.art === "blende") {
      spiel(neu.current, [{ opacity: 0 }, { opacity: 1 }], { dauer: DAUER.antwort.ruhig.zahlTausch, kurve: kurve("expo"), art: "blende" });
      const a = spiel(weg.current, [{ opacity: 1 }, { opacity: 0 }], { dauer: DAUER.antwort.ruhig.zahlTausch, kurve: kurve("expo"), art: "blende" });
      if (a) void a.finished.then(() => setAlt(null), () => undefined);
      else setAlt(null);
      return;
    }
    // Versatz „40 %“ (ruhig „45 %“) aus tokens.json, Richtung nach Zu- oder Abnahme
    const versatz = DAUER.zahlenRollen.versatz[welt.fassung].replace(" ", "");
    const hin = (vz: number) => `translateY(${vz < 0 ? "-" : ""}${versatz})`;
    spiel(neu.current, [{ transform: hin(-auf), opacity: 0 }, { transform: "none", opacity: 1 }], { dauer: DAUER.zahlenRollen.rein, kurve: kurve("expo"), art: "lage" });
    const a = spiel(weg.current, [{ transform: "none", opacity: 1 }, { transform: hin(auf), opacity: 0 }], {
      dauer: DAUER.zahlenRollen.raus,
      kurve: kurve("expo"),
      art: "lage",
    });
    // spiel() entscheidet über reduzierte Bewegung (E9): ohne Animation steht die neue Zahl sofort allein
    if (a) void a.finished.then(() => setAlt(null), () => undefined);
    else setAlt(null);
  }, [alt, props.wert, props.art, welt.fassung]);

  // Hochzählen beim Auftritt: einmal je Einhängen, sobald nicht mehr gewartet wird; der Endwert steht unsichtbar daneben
  // (hält die Breite, wird vorgelesen), der laufende Wert ist stumm
  const fmt = props.format ?? ((n: number) => String(Math.round(n)));
  const [zwischen, setZwischen] = useState<string | null>(() => (props.hochzaehlen && props.wert && !still() && !bewegungReduziert() ? fmt(0) : null));
  useLayoutEffect(() => {
    if (!props.hochzaehlen || props.warten || !props.wert || still()) return;
    if (bewegungReduziert()) {
      spiel(neu.current, [{ opacity: 0 }, { opacity: 1 }], { dauer: BLENDE_REDUZIERT, kurve: kurve("expo"), art: "blende" });
      return;
    }
    const ziel = props.wert;
    const dauer = DAUER.antwort.lebhaft.zaehlen;
    let start = performance.now() + (props.verzug ?? 0);
    let rahmen = 0;
    let aus = false;
    const schritt = (t: number) => {
      const u = Math.min(1, Math.max(0, (t - start) / dauer));
      // expo aus (cubic-bezier(0.16, 1, 0.3, 1) angenähert)
      if (u < 1) {
        setZwischen(fmt(ziel * (1 - Math.pow(2, -10 * u))));
        rahmen = requestAnimationFrame(schritt);
      } else setZwischen(null);
    };
    if (props.takt)
      void props.takt(neu.current).then(
        (t) => {
          if (aus) return;
          start = t + (props.verzug ?? 0);
          rahmen = requestAnimationFrame(schritt);
        },
        () => undefined,
      );
    else rahmen = requestAnimationFrame(schritt);
    return () => {
      aus = true;
      cancelAnimationFrame(rahmen);
      setZwischen(null);
    };
    // nur beim Einhängen (Auftritt) oder am Ende des Wartens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.hochzaehlen, props.warten]);

  // fällt `hochzaehlen` weg (Abbruch des Auftritts), steht der Endwert
  if (zwischen !== null && props.hochzaehlen) {
    return (
      <span className={`zahl-w ${props.className ?? ""}`} data-testid={props.kennung} data-zaehlt="">
        <span aria-hidden="true">{zwischen}</span>
        <span ref={neu} className="zahl-halt">
          {text}
        </span>
      </span>
    );
  }

  return (
    <span className={`zahl-w ${props.className ?? ""}`} data-testid={props.kennung}>
      {alt && (
        <span ref={weg} aria-hidden="true">
          {alt.text}
        </span>
      )}
      <span ref={neu}>{text}</span>
    </span>
  );
}
